import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, Loader2, Archive, ArchiveRestore, Send, Pencil, Check, X, Plus, ChevronDown, ChevronUp, Search, Eye, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  useCoachLibraryWorkbench, useUpdateLibraryItem, usePublishLibraryVersion,
  useArchiveLibraryItem, useUnarchiveLibraryItem, useDeleteLibraryItem, useLibraryCatalog,
} from "@/lib/coachWorkbenchQueries";
import { LibraryCreateForm } from "./library/LibraryCreateForm";
import { LibraryItemManagePanel } from "./library/LibraryItemManagePanel";
import { LibraryAdvancedFilters, EMPTY_FILTERS } from "./library/LibraryAdvancedFilters";
import { LibraryQuickPreviewModal } from "./library/LibraryQuickPreviewModal";
import { SimpleDeleteConfirm } from "./shared/SimpleDeleteConfirm";

const CONTENT_TYPES = ["exercise", "framework", "template", "guide", "checklist"];

/**
 * Master v5 Prompt 19 minimal CRUD + Master v5 Prompt 20 (checkpoints
 * 1 & 2) typed catalog/lifecycle/search/Quick Preview. "My Library" is
 * this coach's own authoring list (every status); "Catalog" is the
 * full shared, search-filtered catalog with 8 smart-view tabs and
 * every V5 search dimension.
 */
export default function CoachLibraryWorkbenchPage() {
  const [tab, setTab] = useState("mine");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedByTab, setExpandedByTab] = useState({ mine: null, catalog: null });
  const expandedId = expandedByTab[tab];
  const setExpandedId = (libId) => setExpandedByTab((prev) => ({ ...prev, [tab]: libId }));
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [view, setView] = useState("");
  const [previewRow, setPreviewRow] = useState(null);

  const mine = useCoachLibraryWorkbench(includeArchived);
  const catalog = useLibraryCatalog(tab === "catalog" ? { ...filters, view } : {});
  const { data, isPending, isError } = tab === "mine" ? mine : catalog;

  const updateItem = useUpdateLibraryItem();
  const publishVersion = usePublishLibraryVersion();
  const archiveItem = useArchiveLibraryItem();
  const unarchiveItem = useUnarchiveLibraryItem();
  const deleteItem = useDeleteLibraryItem();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const items = data?.items || [];

  async function handleRename(libId) {
    try {
      await updateItem.mutateAsync({ libId, title: editTitle.trim() });
      setEditingId(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't rename");
    }
  }

  async function handlePublish(libId, libvId) {
    try {
      const res = await publishVersion.mutateAsync({ libId, libvId });
      if (res?.duplicate_warning?.length) {
        toast.warning(`Published — but this looks identical to ${res.duplicate_warning.map((d) => `${d.human_code} "${d.title}"`).join(", ")}. Nothing was merged.`);
      } else {
        toast.success("Published — now visible in the client-facing Library catalog");
      }
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't publish that version");
    }
  }

  async function handleArchiveToggle(item) {
    try {
      if (item.is_archived) await unarchiveItem.mutateAsync(item.lib_id);
      else await archiveItem.mutateAsync(item.lib_id);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't update that item");
    }
  }

  async function handleConfirmDelete() {
    try {
      await deleteItem.mutateAsync(deleteTarget.lib_id);
      toast.success(`"${deleteTarget.title}" was permanently deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't delete that item");
    }
  }

  return (
    <div data-testid="coach-library-workbench-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="font-serif text-2xl text-ink flex items-center gap-2"><BookOpen size={20} className="text-terracotta" /> Library</h1>
          <p className="text-sm text-ink-soft mt-1">Typed, versioned content — exercises, frameworks, templates, guides and checklists.</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)} className="flex items-center gap-1.5" data-testid="library-create-toggle">
          <Plus size={14} /> New item
        </Button>
      </div>

      {showCreate && <div className="mb-4"><LibraryCreateForm onClose={() => setShowCreate(false)} /></div>}

      <div className="flex items-center gap-4 mb-3 border-b border-ink/10">
        <button onClick={() => setTab("mine")} className={`text-sm pb-2 px-1 ${tab === "mine" ? "text-terracotta border-b-2 border-terracotta" : "text-ink-soft"}`} data-testid="library-tab-mine">My Library</button>
        <button onClick={() => setTab("catalog")} className={`text-sm pb-2 px-1 ${tab === "catalog" ? "text-terracotta border-b-2 border-terracotta" : "text-ink-soft"}`} data-testid="library-tab-catalog">Catalog</button>
      </div>

      {tab === "mine" ? (
        <button onClick={() => setIncludeArchived((v) => !v)} className="text-xs text-ink-soft hover:text-ink mb-2 block" data-testid="coach-library-workbench-toggle-archived">
          {includeArchived ? "Hide archived" : "Show archived"}
        </button>
      ) : (
        <>
          <LibraryAdvancedFilters view={view} onViewChange={setView} filters={filters} onFiltersChange={setFilters} />
          <div className="flex items-center gap-2 mb-3 max-w-xl">
            <select value={filters.content_type} onChange={(e) => setFilters((f) => ({ ...f, content_type: e.target.value }))} className="border border-ink/15 rounded-md text-sm px-2 py-1.5 bg-white" data-testid="library-catalog-filter-type">
              <option value="">All types</option>
              {CONTENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="relative flex-1">
              <Search size={13} className="absolute left-2 top-2.5 text-ink-soft" />
              <Input placeholder="Search title, use case, outcome…" value={filters.keyword} onChange={(e) => setFilters((f) => ({ ...f, keyword: e.target.value }))} className="h-8 text-sm pl-7" data-testid="library-catalog-filter-keyword" />
            </div>
          </div>
        </>
      )}

      {isPending ? (
        <div className="flex items-center gap-2 text-ink-soft" data-testid="coach-library-workbench-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="coach-library-workbench-error">Couldn't load Library.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coach-library-workbench-empty">Nothing here yet — create a new item or promote a File from the Files workbench.</p>
      ) : (
        <div className="space-y-2 max-w-2xl" data-testid="coach-library-workbench-list">
          {items.map((row) => {
            const { item, versions, is_favorite } = row;
            const latest = versions[0];
            const isExpanded = expandedId === item.lib_id;
            return (
              <div key={item.lib_id} className="border border-ink/10 bg-white/50 rounded-lg p-3.5" data-testid={`coach-library-workbench-row-${item.lib_id}`}>
                <div className="flex items-center justify-between gap-2">
                  {editingId === item.lib_id ? (
                    <div className="flex items-center gap-1.5 flex-1">
                      <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="h-7 text-sm" data-testid={`coach-library-workbench-title-input-${item.lib_id}`} />
                      <button onClick={() => setEditingId(null)} data-testid={`coach-library-workbench-title-cancel-${item.lib_id}`}><X size={13} className="text-ink-soft" /></button>
                      <button onClick={() => handleRename(item.lib_id)} data-testid={`coach-library-workbench-title-save-${item.lib_id}`}><Check size={13} className="text-terracotta" /></button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-ink flex items-center gap-1.5">
                      {item.title}
                      {tab === "mine" && <button onClick={() => { setEditingId(item.lib_id); setEditTitle(item.title); }} data-testid={`coach-library-workbench-edit-btn-${item.lib_id}`}><Pencil size={11} className="text-ink-soft hover:text-ink" /></button>}
                    </p>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] uppercase tracking-wide text-ink-soft bg-ink/5 rounded px-1.5 py-0.5">{item.content_type}</span>
                    <span className="text-[10px] uppercase tracking-wide bg-ink/5 rounded px-1.5 py-0.5" data-testid={`coach-library-workbench-status-${item.lib_id}`}>{latest?.status || "no version"}</span>
                    {item.scope === "client_specific" && <span className="text-[10px] uppercase tracking-wide text-blue-700 bg-blue-100 rounded px-1.5 py-0.5">Client-specific</span>}
                    {item.is_archived && <span className="text-[10px] uppercase tracking-wide text-red-700 bg-red-100 rounded px-1.5 py-0.5">Archived</span>}
                  </div>
                </div>
                <p className="text-[11px] text-ink-soft mt-1">{item.human_code}</p>
                <div className="flex items-center gap-3 mt-2">
                  {tab === "mine" && latest?.status === "draft" && (
                    <button onClick={() => handlePublish(item.lib_id, latest.libv_id)} className="text-xs text-terracotta hover:underline flex items-center gap-1" data-testid={`coach-library-workbench-publish-${item.lib_id}`}>
                      <Send size={11} /> Publish
                    </button>
                  )}
                  {tab === "mine" && (
                    <button onClick={() => handleArchiveToggle(item)} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1" data-testid={`coach-library-workbench-archive-toggle-${item.lib_id}`}>
                      {item.is_archived ? <><ArchiveRestore size={11} /> Unarchive</> : <><Archive size={11} /> Archive</>}
                    </button>
                  )}
                  {tab === "mine" && item.is_archived && (
                    <button onClick={() => setDeleteTarget(item)} className="text-xs text-ink-soft hover:text-red-600 flex items-center gap-1" data-testid={`coach-library-workbench-delete-${item.lib_id}`}>
                      <Trash2 size={11} /> Delete permanently
                    </button>
                  )}
                  <button onClick={() => setPreviewRow(row)} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1" data-testid={`coach-library-workbench-preview-${item.lib_id}`}>
                    <Eye size={11} /> Preview
                  </button>
                  <button onClick={() => setExpandedId(isExpanded ? null : item.lib_id)} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1 ml-auto" data-testid={`coach-library-workbench-expand-${item.lib_id}`}>
                    {isExpanded ? <>Less <ChevronUp size={11} /></> : <>Manage <ChevronDown size={11} /></>}
                  </button>
                </div>
                {isExpanded && <LibraryItemManagePanel item={item} latest={latest} isFavorite={!!is_favorite} />}
              </div>
            );
          })}
        </div>
      )}

      <LibraryQuickPreviewModal
        item={previewRow?.item} latest={previewRow?.versions?.[0]} isFavorite={!!previewRow?.is_favorite}
        open={!!previewRow} onOpenChange={(open) => !open && setPreviewRow(null)}
      />
      <SimpleDeleteConfirm
        open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this Library item permanently?"
        description={`"${deleteTarget?.title}" and all its versions will be permanently removed. This can't be undone.`}
        onConfirm={handleConfirmDelete} isPending={deleteItem.isPending}
        testId="coach-library-workbench-delete-confirm"
      />
    </div>
  );
}

